import { useEffect, useMemo, useState } from "react";
import { priceRepo, fxRepo, costRepo } from "@/lib/db/pricing";
import { isSupabaseConfigured } from "@/lib/db";
import type { PricePolicyWithTiers } from "@/types";
import {
  blendedUnit,
  calcTotal,
  freeEarned,
  freeLeverage,
  marginPair,
  retroDiff,
  tierBreakdown,
  type PriceTier,
  type PricingMode,
} from "@/lib/pricing/calc";

/**
 * 표준 가격 패널 (pricing-corridor M1) — WO-012 엔진을 /deals/new 에 흡수.
 * 국가 선택 → 표준 티어 → 블렌디드·마진(순/총)·소급차액·레버리지 (USD/KRW 분할).
 * Supabase 미설정/정책 없음이면 시드 동형 폴백으로 오프라인 동작.
 */

const PRODUCT_CODE = "MTX-C1";
const COST_KRW_NET_FALLBACK = 6_000_000;
const FX_FALLBACK = 1500;

interface PanelPolicy {
  countryCode: string;
  countryName: string;
  mode: "prospective" | "flat";
  tiers: PriceTier[];
  freePerN: number | null;
  announce: number | null;
  exclusivity: string | null;
  note: string;
  status: "active" | "draft" | "dead";
  source: "db" | "fallback";
}

const TW_TIERS: PriceTier[] = [
  { qtyFrom: 1, qtyTo: 10, unitPriceUsd: 18000, labelKo: "안내가" },
  { qtyFrom: 11, qtyTo: 19, unitPriceUsd: 17000 },
  { qtyFrom: 20, qtyTo: 29, unitPriceUsd: 16000, labelKo: "총판 기본가" },
  { qtyFrom: 30, qtyTo: 39, unitPriceUsd: 15000 },
  { qtyFrom: 40, qtyTo: 49, unitPriceUsd: 14000 },
  { qtyFrom: 50, qtyTo: null, unitPriceUsd: 13000, labelKo: "최저가" },
];

const FALLBACK: Record<string, PanelPolicy> = {
  TW: { countryCode: "TW", countryName: "대만", mode: "prospective", tiers: TW_TIERS, freePerN: 30, announce: 18000, exclusivity: "독점", note: "v3 확정 · TFDA 전액부담", status: "active", source: "fallback" },
  TH: { countryCode: "TH", countryName: "태국", mode: "flat", tiers: [{ qtyFrom: 1, qtyTo: null, unitPriceUsd: 10000 }], freePerN: 30, announce: 18500, exclusivity: "비독점", note: "계약서 18,500 / 실제 10,000 · 2026-10 만료", status: "active", source: "fallback" },
  IN: { countryCode: "IN", countryName: "인도", mode: "flat", tiers: [{ qtyFrom: 1, qtyTo: null, unitPriceUsd: 10000 }], freePerN: 30, announce: null, exclusivity: "—", note: "v1.3 미서명", status: "draft", source: "fallback" },
};
const COUNTRY_NAME: Record<string, string> = { TW: "대만", TH: "태국", IN: "인도", VN: "베트남" };

function dbToPanel(p: PricePolicyWithTiers): PanelPolicy {
  const tiers: PriceTier[] =
    p.pricing_mode === "flat"
      ? [{ qtyFrom: 1, qtyTo: null, unitPriceUsd: p.flat_price_usd ?? 0 }]
      : p.tiers.map((t) => ({ qtyFrom: t.qty_from, qtyTo: t.qty_to, unitPriceUsd: t.unit_price_usd, labelKo: t.label_ko }));
  return {
    countryCode: p.country_code,
    countryName: COUNTRY_NAME[p.country_code] ?? p.country_code,
    mode: p.pricing_mode,
    tiers,
    freePerN: p.free_per_n,
    announce: p.announce_price_usd,
    exclusivity: p.exclusivity,
    note: p.note,
    status: p.status,
    source: "db",
  };
}

const usd = (n: number) => (Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—");
const krw = (n: number) => (Number.isFinite(n) ? Math.round(n).toLocaleString("ko-KR") : "—");
const eok = (n: number) => (Number.isFinite(n) ? (n / 1e8 >= 10 ? (n / 1e8).toFixed(1) : (n / 1e8).toFixed(2)) + "억" : "—");
const pct = (n: number) => (Number.isFinite(n) ? (n * 100).toFixed(1) + "%" : "—");

function DualCell({ label, u, k, hero }: { label: string; u: string; k: string; hero?: boolean }): JSX.Element {
  return (
    <div className={"pc-dual" + (hero ? " pc-hero" : "")}>
      <div className="pc-dl">{label}</div>
      <div className="pc-cells">
        <div className="pc-cell"><span className="pc-cu">USD</span><span className="pc-cv">{u}</span></div>
        <div className="pc-cell pc-krw"><span className="pc-cu">KRW</span><span className="pc-cv">{k}</span></div>
      </div>
    </div>
  );
}

export function PricingPanel({
  countryCode,
  orderQty,
  onApplyBlended,
}: {
  countryCode?: string;
  orderQty?: number;
  onApplyBlended?: (blendedUsd: number) => void;
}): JSX.Element {
  const initCountry = countryCode && FALLBACK[countryCode] ? countryCode : "TW";
  const [sel, setSel] = useState(initCountry);
  const [policy, setPolicy] = useState<PanelPolicy>(FALLBACK[initCountry]!);
  const [units, setUnits] = useState(Math.max(1, Math.min(200, orderQty || 50)));
  const [mode, setMode] = useState<PricingMode>("prospective");
  const [fx, setFx] = useState(FX_FALLBACK);
  const [costNet, setCostNet] = useState(COST_KRW_NET_FALLBACK);
  const [costFull, setCostFull] = useState<number | null>(null);

  // 폼의 목적국이 정책 보유국이면 패널 국가 동기화
  useEffect(() => {
    if (countryCode && FALLBACK[countryCode]) setSel(countryCode);
  }, [countryCode]);

  // 폼 라인 합계 대수 반영 (사용자가 패널에서 바꾸기 전까지)
  useEffect(() => {
    if (orderQty && orderQty > 0) setUnits(Math.max(1, Math.min(200, orderQty)));
  }, [orderQty]);

  // 정책·환율·원가 로드 (DB 우선, 폴백)
  useEffect(() => {
    let alive = true;
    (async () => {
      const fb = FALLBACK[sel];
      let next: PanelPolicy | undefined = fb;
      try {
        if (isSupabaseConfigured()) {
          const db = await priceRepo.policyFor(PRODUCT_CODE, sel);
          if (db) next = dbToPanel(db);
        }
      } catch {
        /* 폴백 유지 */
      }
      if (alive && next) {
        setPolicy(next);
        setMode(next.mode === "flat" ? "flat" : "prospective");
      }
    })();
    return () => {
      alive = false;
    };
  }, [sel]);

  // 환율·원가 최신값 로드 (best-effort)
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    fxRepo.latest().then((r) => r && setFx(Number(r.rate))).catch(() => {});
    costRepo.latest(PRODUCT_CODE).then((c) => {
      if (c) {
        setCostNet(c.cost_krw_net);
        setCostFull(c.cost_krw_full);
      }
    }).catch(() => {});
  }, []);

  const m = useMemo(() => {
    const isTier = policy.mode === "prospective";
    const effMode: PricingMode = isTier ? mode : "flat";
    const total = calcTotal(units, policy.tiers, effMode);
    const blended = blendedUnit(units, policy.tiers, effMode);
    const mp = marginPair(blended, fx, costNet, costFull);
    const free = freeEarned(units, policy.freePerN);
    const prod = units + free;
    const revenueKrw = total * fx;
    const totalCostKrw = prod * costNet;
    const dealNetKrw = revenueKrw - totalCostKrw;
    const dealNetPct = revenueKrw > 0 ? dealNetKrw / revenueKrw : 0;
    const diff = isTier ? retroDiff(units, policy.tiers) : 0;
    const refPrice = policy.tiers.length ? policy.tiers[policy.tiers.length - 1]!.unitPriceUsd : 0;
    const lev = freeLeverage(refPrice, costNet, fx);
    const bands = isTier && effMode === "prospective" ? tierBreakdown(units, policy.tiers) : [];
    // 다음 티어 / 다음 무상
    let nextTierAt: number | null = null;
    let nextTierPrice: number | null = null;
    if (isTier) {
      const nt = [...policy.tiers].sort((a, b) => a.qtyFrom - b.qtyFrom).find((t) => t.qtyFrom > units);
      if (nt) {
        nextTierAt = nt.qtyFrom;
        nextTierPrice = nt.unitPriceUsd;
      }
    }
    const nextFree = policy.freePerN ? policy.freePerN - (units % policy.freePerN) : null;
    return { isTier, effMode, total, blended, mp, free, prod, revenueKrw, totalCostKrw, dealNetKrw, dealNetPct, diff, lev, refPrice, bands, nextTierAt, nextTierPrice, nextFree };
  }, [policy, units, mode, fx, costNet, costFull]);

  const trig = fx < 1400 ? "crit" : fx < 1450 ? "warn" : "ok";
  const draft = policy.status !== "active";

  return (
    <div className="pc-root">
      <style>{PC_CSS}</style>
      <div className="pc-head">
        <div className="pc-title">
          <span className="pc-badge">내부 전용</span>
          표준 가격 · 마진 <span className="pc-sub">— 협상 5초 계산</span>
        </div>
        <div className="pc-seg" role="group" aria-label="국가">
          {(["TW", "TH", "IN"] as const).map((c) => (
            <button key={c} type="button" className={sel === c ? "on" : ""} onClick={() => setSel(c)}>
              {FALLBACK[c]!.countryName}
            </button>
          ))}
        </div>
      </div>

      <div className="pc-meta">
        <span className={"pc-flag pc-" + policy.countryCode.toLowerCase()}>{policy.countryCode}</span>
        <b>{policy.countryName}</b>
        <span className="pc-tag">{policy.mode === "prospective" ? "계단식(prospective)" : "flat"}</span>
        {policy.exclusivity && policy.exclusivity !== "—" && <span className="pc-tag">{policy.exclusivity}</span>}
        {draft && <span className="pc-tag pc-warn">draft · 미서명</span>}
        {policy.source === "fallback" && <span className="pc-tag pc-muted">폴백값(DB 미연결)</span>}
        <span className="pc-note">{policy.note}</span>
      </div>

      {/* 컨트롤 */}
      <div className="pc-ctl">
        <label className="pc-field">
          <span>대수 {units}</span>
          <input type="range" min={1} max={200} value={units} onChange={(e) => setUnits(+e.target.value)} />
        </label>
        {m.isTier && (
          <div className="pc-seg pc-mode" role="group" aria-label="가격 모드">
            <button type="button" className={mode === "prospective" ? "on" : ""} onClick={() => setMode("prospective")}>향후 조정</button>
            <button type="button" className={mode === "retroactive" ? "on" : ""} onClick={() => setMode("retroactive")}>소급(상대 요구)</button>
          </div>
        )}
        <label className="pc-field">
          <span>
            환율 {krw(fx)}{" "}
            <em className={"pc-trig pc-" + trig}>{trig === "crit" ? "▼ 재협상 트리거" : trig === "warn" ? "관찰" : "정상"}</em>
          </span>
          <input type="range" min={1300} max={1650} step={5} value={fx} onChange={(e) => setFx(+e.target.value)} />
        </label>
        <div className="pc-presets">
          {[1365, 1500, 1562].map((v) => (
            <button key={v} type="button" onClick={() => setFx(v)}>{krw(v)}</button>
          ))}
        </div>
      </div>

      {/* 출력 */}
      <div className="pc-grid">
        <DualCell label="블렌디드 단가" u={"$" + usd(m.blended)} k={"₩" + krw(m.blended * fx)} hero />
        <DualCell label="딜 총액" u={"$" + usd(m.total)} k={"₩" + eok(m.revenueKrw)} />
        <DualCell
          label={"단가 마진율 (순원가 $" + usd(m.mp.costUsdNet) + ")"}
          u={pct(m.mp.marginPctNet)}
          k={m.mp.marginPctFull != null ? "총원가 " + pct(m.mp.marginPctFull) : "총원가 입력대기"}
        />
        <DualCell label="딜 순마진 (무상 원가 포함)" u={pct(m.dealNetPct)} k={"₩" + eok(m.dealNetKrw)} />
      </div>

      <div className="pc-rows">
        {m.isTier && (
          <div className={"pc-diff" + (m.diff > 0 ? "" : " pc-flat")}>
            <b>{m.diff > 0 ? "−$" + usd(m.diff) : "—"}</b>
            <span>소급 요구 방어선 — "처음부터 최저가 해줘" 시 손실{m.diff > 0 ? " (대당 $" + usd(m.blended - 13000) + ")" : ""}</span>
          </div>
        )}
        <div className="pc-lev">
          <b>{m.lev.toFixed(2)}×</b>
          <span>무상 레버리지 (체감 ${usd(m.refPrice)} / 실비 ${usd(m.mp.costUsdNet)}) — 가격보다 무상을 먼저 태울 것</span>
        </div>
        <div className="pc-mini">
          <span>생산 {m.prod}대 (유상 {units} + 무상 {m.free})</span>
          {m.nextFree != null && <span>다음 무상까지 {m.nextFree}대</span>}
          {m.isTier && <span>{m.nextTierAt ? `다음 티어 ${m.nextTierAt}대 → $${usd(m.nextTierPrice ?? 0)}` : "최저가 도달"}</span>}
        </div>
      </div>

      {/* 계단 내역 */}
      {m.bands.length > 0 && (
        <details className="pc-bd">
          <summary>계단 내역 ({m.bands.length}구간) — 합 ${usd(m.total)}</summary>
          <table>
            <thead><tr><th>구간</th><th>대수</th><th>단가</th><th>소계</th></tr></thead>
            <tbody>
              {m.bands.map((b, i) => (
                <tr key={i} className={b.labelKo ? "lbl" : ""}>
                  <td>{b.qtyFrom}{b.qtyTo != null ? `–${b.qtyTo}` : "+"}{b.labelKo ? ` · ${b.labelKo}` : ""}</td>
                  <td>{b.span}</td>
                  <td>${usd(b.unitPriceUsd)}</td>
                  <td>${usd(b.subtotalUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {onApplyBlended && (
        <div className="pc-apply">
          <button type="button" onClick={() => onApplyBlended(Math.round(m.blended))}>
            블렌디드 ${usd(m.blended)} 를 라인 단가에 적용
          </button>
          <span className="pc-note">협상은 순원가 기준. 세율은 어떤 값도 산출하지 않음(선적시점 통관사 확인).</span>
        </div>
      )}
    </div>
  );
}

const PC_CSS = `
.pc-root{--pc-ac:#0e8578;--pc-ink:#16202b;--pc-ink2:#5b6a7a;--pc-line:#dfe6ec;--pc-surf:#fff;--pc-surf2:#f4f8f9;--pc-crit:#c33a3a;--pc-warn:#a9760a;--pc-good:#0a8a0a;
  font:14px/1.5 system-ui,-apple-system,"Segoe UI","Malgun Gothic",sans-serif;color:var(--pc-ink);background:linear-gradient(180deg,#f7fbfb,#eef4f5);
  border:1px solid var(--pc-line);border-radius:14px;padding:16px 18px;margin:14px 0}
.pc-root *{box-sizing:border-box}
.pc-mono{font-family:ui-monospace,"SF Mono",Consolas,monospace}
.pc-head{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:12px}
.pc-title{font-weight:750;font-size:15px;display:flex;align-items:center;gap:9px}
.pc-title .pc-sub{color:var(--pc-ink2);font-weight:500}
.pc-badge{font-size:10.5px;font-weight:800;letter-spacing:.03em;color:var(--pc-crit);background:rgba(195,58,58,.1);border:1px solid rgba(195,58,58,.28);border-radius:999px;padding:2px 8px}
.pc-seg{display:inline-flex;background:#e6edee;border-radius:999px;padding:3px;gap:2px}
.pc-seg button{font:inherit;font-size:12.5px;font-weight:650;color:var(--pc-ink2);background:transparent;border:0;border-radius:999px;padding:5px 12px;cursor:pointer}
.pc-seg button.on{background:#fff;color:var(--pc-ac);box-shadow:0 1px 3px rgba(0,0,0,.12)}
.pc-seg button:focus-visible{outline:2px solid var(--pc-ac);outline-offset:1px}
.pc-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:12.5px;color:var(--pc-ink2);margin-bottom:12px;padding-bottom:11px;border-bottom:1px solid var(--pc-line)}
.pc-meta b{color:var(--pc-ink)}
.pc-flag{font-size:11px;font-weight:800;color:#fff;border-radius:5px;padding:2px 6px}
.pc-flag.pc-tw{background:#2a78d6}.pc-flag.pc-th{background:#c78a12}.pc-flag.pc-in{background:#4a3aa7}.pc-flag.pc-vn{background:#cf5b86}
.pc-tag{font-size:11px;font-weight:650;background:#e9eff0;border-radius:6px;padding:2px 7px;color:var(--pc-ink2)}
.pc-tag.pc-warn{color:var(--pc-warn);background:rgba(169,118,10,.12)}
.pc-tag.pc-muted{color:#8a94a3;background:#eef2f3}
.pc-note{color:#8a94a3;font-size:11.5px}
.pc-ctl{display:flex;align-items:flex-end;gap:16px;flex-wrap:wrap;margin-bottom:14px}
.pc-field{display:flex;flex-direction:column;gap:5px;font-size:12px;color:var(--pc-ink2);min-width:180px;flex:1}
.pc-field span{font-weight:600}
.pc-field em{font-style:normal;font-weight:700}
.pc-trig.pc-crit{color:var(--pc-crit)}.pc-trig.pc-warn{color:var(--pc-warn)}.pc-trig.pc-ok{color:var(--pc-good)}
.pc-root input[type=range]{-webkit-appearance:none;appearance:none;height:4px;border-radius:3px;background:#cdd8dc;cursor:pointer;width:100%}
.pc-root input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:var(--pc-ac);border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3)}
.pc-root input[type=range]::-moz-range-thumb{width:16px;height:16px;border-radius:50%;background:var(--pc-ac);border:2px solid #fff}
.pc-mode button{font-size:11.5px;padding:5px 10px}
.pc-presets{display:flex;gap:5px}
.pc-presets button{font:inherit;font-size:11px;font-weight:600;color:var(--pc-ink2);background:var(--pc-surf);border:1px solid var(--pc-line);border-radius:7px;padding:5px 8px;cursor:pointer}
.pc-presets button:hover{border-color:var(--pc-ac);color:var(--pc-ink)}
.pc-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin-bottom:11px}
.pc-dual{border:1px solid var(--pc-line);border-radius:10px;background:var(--pc-surf);overflow:hidden}
.pc-dual.pc-hero{background:rgba(14,133,120,.08);border-color:rgba(14,133,120,.3)}
.pc-dl{font-size:10px;font-weight:700;letter-spacing:.04em;color:var(--pc-ink2);padding:7px 10px 2px;text-transform:uppercase}
.pc-cells{display:grid;grid-template-columns:1fr 1fr}
.pc-cell{padding:1px 10px 9px;display:flex;flex-direction:column;gap:1px}
.pc-cell.pc-krw{border-left:1px solid var(--pc-line)}
.pc-cu{font-size:9.5px;font-weight:700;color:#9aa4b1}
.pc-cv{font-family:ui-monospace,"SF Mono",Consolas,monospace;font-weight:700;font-size:15px;font-variant-numeric:tabular-nums;letter-spacing:-.01em}
.pc-dual.pc-hero .pc-cv{color:var(--pc-ac)}
.pc-rows{display:flex;flex-direction:column;gap:8px;margin-bottom:12px}
.pc-diff,.pc-lev{display:flex;align-items:center;gap:12px;padding:10px 13px;border-radius:9px;font-size:12px;color:var(--pc-ink2)}
.pc-diff{background:rgba(195,58,58,.08);border:1px solid rgba(195,58,58,.24)}
.pc-diff.pc-flat{background:var(--pc-surf2);border-color:var(--pc-line)}
.pc-diff b{font-family:ui-monospace,Consolas,monospace;font-weight:800;font-size:18px;color:var(--pc-crit);white-space:nowrap}
.pc-diff.pc-flat b{color:#9aa4b1;font-size:14px}
.pc-lev{background:var(--pc-surf2);border:1px solid var(--pc-line)}
.pc-lev b{font-family:ui-monospace,Consolas,monospace;font-weight:800;font-size:18px;color:var(--pc-ac)}
.pc-mini{display:flex;gap:16px;flex-wrap:wrap;font-size:12px;color:var(--pc-ink2);padding:0 2px}
.pc-mini span{font-variant-numeric:tabular-nums}
.pc-bd{margin-bottom:10px;font-size:12px}
.pc-bd summary{cursor:pointer;color:var(--pc-ink2);font-weight:600;padding:6px 0}
.pc-bd table{width:100%;border-collapse:collapse;margin-top:4px}
.pc-bd th{text-align:right;font-size:10px;font-weight:700;color:#9aa4b1;text-transform:uppercase;padding:4px 8px;border-bottom:1px solid var(--pc-line)}
.pc-bd th:first-child{text-align:left}
.pc-bd td{text-align:right;font-family:ui-monospace,Consolas,monospace;font-variant-numeric:tabular-nums;padding:5px 8px;border-bottom:1px solid var(--pc-line)}
.pc-bd td:first-child{text-align:left;font-family:inherit;color:var(--pc-ink2)}
.pc-bd tr.lbl td:first-child{color:var(--pc-ink);font-weight:600}
.pc-apply{display:flex;align-items:center;gap:12px;flex-wrap:wrap;border-top:1px dashed var(--pc-line);padding-top:12px}
.pc-apply button{font:inherit;font-weight:650;font-size:13px;color:#fff;background:var(--pc-ac);border:0;border-radius:8px;padding:9px 15px;cursor:pointer}
.pc-apply button:hover{background:#0a6e63}
.pc-apply button:focus-visible{outline:2px solid var(--pc-ac);outline-offset:2px}
@media (max-width:760px){.pc-grid{grid-template-columns:1fr 1fr}}
`;
